/**
 * Product Card Component
 * Compact card with image, name, price, and quick add to cart
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from '../../hooks/useTranslation';

interface Product {
  id: number;
  name: string;
  description?: string;
  price: number;
  compare_at_price?: number | null;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  image_urls?: string[];
  stock_quantity?: number;
  track_inventory?: boolean;
  is_featured?: boolean;
}

interface ProductCardProps {
  product: Product;
  currency?: string;
  currencySymbol?: string;
  onAddToCart?: (productId: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  currency = 'USD',
  currencySymbol = '$',
  onAddToCart
}) => {
  const navigate = useNavigate();
  const { botUsername } = useParams<{ botUsername: string }>();
  const [isAdding, setIsAdding] = useState(false);
  const { t } = useTranslation();

  // Translate product type
  const getProductTypeLabel = (type: string): string => {
    const typeKey = `shop.productTypes.${type}`;
    const translated = t(typeKey);
    // If translation not found, return the type as-is with capitalization
    return translated === typeKey ? type.charAt(0).toUpperCase() + type.slice(1) : translated;
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on add to cart button
    if ((e.target as HTMLElement).closest('.add-cart-btn')) return;
    navigate(`/shop/${botUsername}/product/${product.id}`);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOutOfStock || isAdding) return;

    setIsAdding(true);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: product.id,
          quantity: 1
        })
      });

      const data = await res.json();
      if (data.success) {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        if (onAddToCart) onAddToCart(product.id);
      } else {
        window.Telegram?.WebApp?.showAlert(data.error || 'Failed to add');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `${currencySymbol}${(numPrice || 0).toFixed(2)}`;
  };

  const isOutOfStock = product.track_inventory && (product.stock_quantity ?? 0) <= 0;
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compare_at_price! - product.price) / product.compare_at_price!) * 100)
    : 0;

  const imageUrl = product.image_urls?.[0] || null;

  return (
    <CardContainer onClick={handleClick}>
      <ImageContainer>
        {imageUrl ? (
          <ProductImage src={imageUrl} alt={product.name} loading="lazy" />
        ) : (
          <PlaceholderImage>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
          </PlaceholderImage>
        )}

        {hasDiscount && (
          <DiscountBadge>-{discountPercent}%</DiscountBadge>
        )}

        {isOutOfStock && (
          <OutOfStockOverlay>
            <span>{t('shop.outOfStock')}</span>
          </OutOfStockOverlay>
        )}
      </ImageContainer>

      <CardContent>
        <TopRow>
          <TypeBadge $type={product.product_type}>{getProductTypeLabel(product.product_type)}</TypeBadge>
        </TopRow>
        <ProductName>{product.name}</ProductName>
        <PriceRow>
          <CurrentPrice>{formatPrice(product.price)}</CurrentPrice>
          {hasDiscount && (
            <OriginalPrice>{formatPrice(product.compare_at_price!)}</OriginalPrice>
          )}
        </PriceRow>

        {!isOutOfStock && (
          <AddToCartBtn
            className="add-cart-btn"
            onClick={handleAddToCart}
            disabled={isAdding}
          >
            {isAdding ? (
              <SmallSpinner />
            ) : (
              <>
                <CartIcon>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-8.9-5h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L17.31 4l-3.86 7H8.53L4.27 2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2z"/>
                  </svg>
                </CartIcon>
                {t('common.add')}
              </>
            )}
          </AddToCartBtn>
        )}
      </CardContent>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.98);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 75%;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
`;

const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tg-theme-hint-color, #999);
`;

const DiscountBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ff3b30;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 3px;
`;

const OutOfStockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }
`;

const CardContent = styled.div`
  padding: 6px 8px 8px;
`;

const TopRow = styled.div`
  margin-bottom: 2px;
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF5020';
      case 'downloadable': return '#2196F320';
      case 'keys': return '#9C27B020';
      case 'subscription': return '#FF980020';
      default: return '#99999920';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF50';
      case 'downloadable': return '#2196F3';
      case 'keys': return '#9C27B0';
      case 'subscription': return '#FF9800';
      default: return '#999';
    }
  }};
`;

const ProductName = styled.h3`
  font-size: 12px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 2px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const CurrentPrice = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--tg-theme-button-color, #3390ec);
`;

const OriginalPrice = styled.span`
  font-size: 11px;
  color: var(--tg-theme-hint-color, #999);
  text-decoration: line-through;
`;

const AddToCartBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:active {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CartIcon = styled.span`
  display: flex;
  align-items: center;
`;

const SmallSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default ProductCard;
